import { useMutation, useQuery } from '@tanstack/react-query'
import { Bot, Loader2, Mic, Send, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { useOverlayStore } from '../lib/overlay-store'
import { recordingFilename, useAudioRecorder } from '../lib/use-audio-recorder'
import { useIsMobile, useIsTabletBand } from '../lib/use-media-query'
import { useSheetMaxHeight } from '../lib/use-visual-viewport'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolActions?: Array<{
    name: string
    success: boolean
    summary: string
  }>
}

export function AiAssistant() {
  const { data: config } = useQuery({
    queryKey: ['ai', 'config'],
    queryFn: () => api.getAiConfig(),
  })

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const recorder = useAudioRecorder()
  const transcribe = useMutation({
    mutationFn: (rec: { blob: Blob; mimeType: string }) =>
      api.transcribeAudio(rec.blob, recordingFilename(rec.mimeType)),
    // Transkript landet EDITIERBAR im Eingabefeld — der Agent bleibt
    // textbasiert, Voice ist reine Eingabeschicht.
    onSuccess: (r) => setInput((prev) => (prev ? `${prev} ${r.text}` : r.text)),
    onError: (err: Error) => notify.error(err.message),
  })
  useEffect(() => {
    if (recorder.error) notify.error(recorder.error)
  }, [recorder.error])
  const scrollRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  // Sheet-Höhe am sichtbaren Viewport (identisch zu dialog/side-panel) —
  // Umzug aufs Sheet-Primitive ist ein späteres Refactoring.
  const sheetMaxHeight = useSheetMaxHeight()
  const overlayOpen = useOverlayStore((s) => s.openCount > 0)
  // Auf Phones (hoch + quer) UND im Tablet-Band 640–1023 (T2) belegt auf
  // JEDER eingeloggten Route die MobileShellBottom-Bar den unteren Rand —
  // die Bubble muss darüber (Klasse + Media-Query in index.css, deren
  // Breiten-Zweig bis 1023.98 reicht). Gleicher Cache-Key wie __root,
  // kein zusätzlicher Request.
  const isTabletBand = useIsTabletBand()
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const liftAboveMobileBar = (isMobile || isTabletBand) && !!me?.user

  const chatMutation = useMutation({
    mutationFn: (userMessages: Array<{ role: string; content: string }>) =>
      api.aiChat(userMessages),
    onSuccess: (data) => {
      const textParts: string[] = []
      const toolActions: ChatMessage['toolActions'] = []

      for (const event of data.events) {
        if (event.type === 'text' && event.content) {
          textParts.push(event.content)
        }
        if (event.type === 'tool_call') {
          toolActions.push({
            name: event.name ?? 'action',
            success: event.success ?? false,
            summary: event.summary ?? '',
          })
        }
      }

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: textParts.join(''),
        toolActions: toolActions.length > 0 ? toolActions : undefined,
      }

      setMessages((prev) => [...prev, assistantMsg])
    },
    onError: (err) => {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Fehler: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`,
        },
      ])
    },
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every new message / pending change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, chatMutation.isPending])

  if (!config?.configured) return null

  const handleSend = () => {
    const text = input.trim()
    if (!text || chatMutation.isPending) return

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
    }

    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    setInput('')

    const apiMessages = updatedMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }))

    chatMutation.mutate(apiMessages)
  }

  // Chat content shared between mobile bottom-sheet and desktop panel
  const chatContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default bg-bg-elevated shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-accent" />
          <span className="font-semibold text-text-primary/90 text-sm">KI-Assistent</span>
          <span className="text-xs text-text-secondary">({config.model})</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-2 text-text-secondary hover:text-text-primary/90 transition-colors touch:opacity-100 rounded-md hover:bg-bg-overlay"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-3 min-h-0"
      >
        {messages.length === 0 && (
          <div className="text-center text-text-secondary text-sm py-8">
            <Bot className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>Wie kann ich dir helfen?</p>
            <p className="text-xs mt-1 opacity-60">
              z.B. &bdquo;Starte einen Timer für Projektarbeit&ldquo;
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                msg.role === 'user' ? 'bg-accent text-white' : 'bg-bg-base text-text-primary/90'
              }`}
            >
              {msg.toolActions?.map((action) => (
                <div
                  key={`${msg.id}-${action.name}-${action.summary}`}
                  className={`flex items-center gap-1.5 text-xs mb-1.5 px-2 py-1 rounded ${
                    action.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  <span>{action.success ? '✓' : '✗'}</span>
                  <span>{action.summary}</span>
                </div>
              ))}
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}

        {chatMutation.isPending && (
          <div className="flex justify-start">
            <div className="bg-bg-base text-text-secondary rounded-xl px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Denkt nach …</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-border-default px-3 py-2 shrink-0">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="Nachricht eingeben…"
            disabled={chatMutation.isPending}
            className="flex-1 bg-bg-base text-text-primary/90 rounded-lg px-3 py-2 text-base lg:text-sm placeholder:text-text-secondary outline-none focus:ring-1 focus:ring-accent border border-border-default"
          />
          {config?.stt?.configured && recorder.supported && (
            <button
              type="button"
              onClick={async () => {
                if (recorder.recording) {
                  const rec = await recorder.stop()
                  if (rec) transcribe.mutate(rec)
                } else {
                  recorder.start()
                }
              }}
              disabled={chatMutation.isPending || transcribe.isPending}
              aria-label={recorder.recording ? 'Aufnahme beenden' : 'Spracheingabe'}
              title={recorder.recording ? 'Aufnahme beenden' : 'Spracheingabe'}
              className={
                recorder.recording
                  ? 'h-9 w-9 rounded-lg bg-danger text-white flex items-center justify-center animate-pulse transition-colors'
                  : 'h-9 w-9 rounded-lg border border-border-default text-text-secondary flex items-center justify-center hover:text-text-primary hover:bg-bg-overlay/60 disabled:opacity-40 transition-colors'
              }
            >
              {transcribe.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || chatMutation.isPending}
            aria-label="Nachricht senden"
            title="Nachricht senden"
            className="h-9 w-9 rounded-lg bg-accent text-white flex items-center justify-center hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch:opacity-100"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  )

  if (!open) {
    // Hide the FAB when any bottom sheet / side panel is open to avoid
    // z-index fights and visual clutter on the overlay backdrop.
    if (overlayOpen) return null

    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Positionierung JS-gated statt sm:-CSS: im Handy-Querformat (≥640
        // BREIT, aber isMobile) gewänne sonst die frühere sm:-Position
        // (1.5rem) gegen die .ai-fab-above-mobile-bar-Regel (index.css
        // steht im Bundle VOR den Breakpoint-Utilities) und die Bubble
        // säße IN der Bottom-Bar.
        className={`fixed ${isMobile ? 'bottom-20 right-4' : 'bottom-6 right-6'} z-50 h-14 w-14 rounded-full bg-accent text-white shadow-lg hover:bg-accent/90 transition-all flex items-center justify-center hover:scale-105 touch:opacity-100 ai-fab${
          liftAboveMobileBar ? ' ai-fab-above-mobile-bar' : ''
        }`}
        title="KI-Assistent"
      >
        <Bot className="h-6 w-6" />
      </button>
    )
  }

  // Mobile: bottom-sheet pattern (matches SidePanel mobile behavior)
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
        <div
          className="absolute inset-0 bg-black/50 animate-fade-in"
          onClick={() => setOpen(false)}
        />
        <div
          className="app-safe-inline absolute bottom-0 left-0 right-0 bg-bg-elevated rounded-t-2xl flex flex-col animate-slide-up ai-panel"
          style={{
            maxHeight: sheetMaxHeight,
            paddingBottom: 'var(--app-safe-bottom)',
          }}
        >
          {/* Drag handle */}
          <div className="flex justify-center py-2 shrink-0">
            <div className="w-10 h-1 rounded-full bg-text-tertiary/30" />
          </div>
          {chatContent}
        </div>
      </div>
    )
  }

  // Desktop / tablet: floating panel
  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[520px] bg-bg-elevated border border-border-default rounded-2xl shadow-2xl flex flex-col overflow-hidden ai-panel">
      {chatContent}
    </div>
  )
}
